import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api/client';
import { Booking, Table } from '@/types/inde_menu';

// ============================================================================
// QUERIES
// ============================================================================

export const useBookings = (params?: { restaurantId?: string; customerId?: string; date?: string }) => {
  return useQuery({
    queryKey: ['bookings', params],
    queryFn: async (): Promise<Booking[]> => {
      const { data } = await apiClient.get('/bookings', { params });
      return data;
    },
  });
};

export const useBookingById = (bookingId?: string) => {
  return useQuery({
    queryKey: ['bookings', bookingId],
    queryFn: async (): Promise<Booking> => {
      const { data } = await apiClient.get(`/bookings/${bookingId}`);
      return data;
    },
    enabled: !!bookingId,
  });
};

export const useTables = (restaurantId?: string) => {
  return useQuery({
    queryKey: ['tables', restaurantId],
    queryFn: async (): Promise<Table[]> => {
      if (!restaurantId) return [];
      const { data } = await apiClient.get(`/tables?restaurant_id=${restaurantId}`);
      return data;
    },
    enabled: !!restaurantId,
  });
};

export const useWaiters = (restaurantId?: string) => {
  return useQuery({
    queryKey: ['waiters', restaurantId],
    queryFn: async (): Promise<any[]> => {
      if (!restaurantId) return [];
      const { data } = await apiClient.get(`/waiters?restaurant_id=${restaurantId}`);
      return data;
    },
    enabled: !!restaurantId,
  });
};

// ============================================================================
// MUTATIONS
// ============================================================================

export const useCreateBooking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (bookingData: Partial<Booking>) => {
      const { data } = await apiClient.post('/bookings', bookingData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
};

export const useUpdateBooking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Booking> }) => {
      const { data } = await apiClient.put(`/bookings/${id}`, updates);
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['bookings', variables.id] });
    },
  });
};

export const useUpdateTableStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status, bookingId }: { id: string; status: string; bookingId?: string }) => {
      const { data } = await apiClient.patch(`/tables/${id}/status`, { status, bookingId });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tables'] });
    },
  });
};
